import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Calendar,
  CheckCircle2,
  Loader2,
  User,
  Mail,
  Phone,
  MessageSquare,
  Clock,
  ArrowRight,
  Upload,
  FileText,
  X
} from 'lucide-react';

export const Consultation = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    current_situation: '',
    preferred_time: '',
    questions: '',
    resume_path: '',
    resume_filename: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const allowedExtensions = ['.pdf', '.doc', '.docx'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext)) {
      setError('Please upload a PDF, DOC, or DOCX file.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB.');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload-resume`, {
        method: 'POST',
        body: formDataUpload,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload resume');
      }

      const result = await response.json();
      setFormData(prev => ({
        ...prev,
        resume_path: result.path,
        resume_filename: result.filename
      }));
    } catch (err) {
      setError(err.message || 'Failed to upload resume. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const removeResume = () => {
    setFormData(prev => ({
      ...prev,
      resume_path: '',
      resume_filename: ''
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consultations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Read response body only once
      const responseText = await response.text();

      if (!response.ok) {
        let errorMessage = 'Something went wrong. Please try again.';
        try {
          const data = JSON.parse(responseText);
          errorMessage = data.detail || errorMessage;
        } catch {
          // Response wasn't JSON, use status code
          errorMessage = `Request failed (${response.status}). Please try again.`;
        }
        throw new Error(errorMessage);
      }

      setIsSubmitted(true);
    } catch (err) {
      // Handle network errors and other exceptions
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Failed to book consultation. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSubmitted) {
    return (
      <div data-testid="consultation-page" className="pt-20">
        <section className="min-h-[80vh] flex items-center bg-gradient-to-br from-slate-50 via-white to-blue-50 py-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} className="text-green-600" />
            </div>
            <h1 className="font-outfit font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
              Consultation Booked!
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Thank you for booking a free consultation. We'll reach out within 24 hours to schedule your call.
            </p>
            <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-8">
              <h3 className="font-semibold text-slate-900 mb-4">What to expect:</h3>
              <div className="space-y-3 text-left">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">We'll call you at your preferred time</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">The call will be 15-20 minutes</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">We'll answer all your questions about the program</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button
                  variant="outline"
                  className="border-2 border-slate-200 hover:border-slate-300 rounded-full px-8 py-3"
                >
                  Back to Home
                </Button>
              </Link>
              <Link to="/apply">
                <Button className="bg-[#0066FF] hover:bg-blue-700 text-white rounded-full px-8 py-3">
                  Apply Now
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="consultation-page" className="pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Calendar size={16} />
            <span>Free 15-minute call</span>
          </div>
          <h1 className="font-outfit font-bold text-4xl sm:text-5xl text-slate-900 leading-tight mb-4">
            Book a Free{' '}
            <span className="text-[#0066FF]">Consultation</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Not sure if Elevate Careers is right for you? Let's chat! Book a free consultation and get all your questions answered.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8">
                <h2 className="font-outfit font-bold text-2xl text-slate-900 mb-6">
                  Schedule Your Call
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name & Email Row */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name" className="text-slate-700 font-medium flex items-center gap-2">
                        <User size={16} className="text-slate-400" />
                        Full Name *
                      </Label>
                      <Input
                        id="full_name"
                        name="full_name"
                        type="text"
                        placeholder="Enter your full name"
                        value={formData.full_name}
                        onChange={handleChange}
                        required
                        className="h-12 rounded-xl border-slate-200 focus:border-[#0066FF] focus:ring-[#0066FF]"
                        data-testid="input-full-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700 font-medium flex items-center gap-2">
                        <Mail size={16} className="text-slate-400" />
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="h-12 rounded-xl border-slate-200 focus:border-[#0066FF] focus:ring-[#0066FF]"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 font-medium flex items-center gap-2">
                      <Phone size={16} className="text-slate-400" />
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="9876543210"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-xl border-slate-200 focus:border-[#0066FF] focus:ring-[#0066FF]"
                      data-testid="input-phone"
                    />
                  </div>

                  {/* Current Situation */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Where are you in your career journey? *</Label>
                    <Select
                      value={formData.current_situation}
                      onValueChange={(value) => handleSelectChange('current_situation', value)}
                      required
                    >
                      <SelectTrigger 
                        className="h-12 rounded-xl border-slate-200 focus:border-[#0066FF] focus:ring-[#0066FF]"
                        data-testid="select-situation"
                      >
                        <SelectValue placeholder="Select your situation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Student - Final Year">Student - Final Year</SelectItem>
                        <SelectItem value="Student - Pre-Final Year">Student - Pre-Final Year</SelectItem>
                        <SelectItem value="Recent Graduate">Recent Graduate (0-1 year)</SelectItem>
                        <SelectItem value="Job Seeker">Actively Looking for Jobs</SelectItem>
                        <SelectItem value="Working Professional">Working but want to switch</SelectItem>
                        <SelectItem value="Career Break">On a Career Break</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Preferred Time */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium flex items-center gap-2">
                      <Clock size={16} className="text-slate-400" />
                      Preferred Call Time (Optional)
                    </Label>
                    <Select
                      value={formData.preferred_time}
                      onValueChange={(value) => handleSelectChange('preferred_time', value)}
                    >
                      <SelectTrigger 
                        className="h-12 rounded-xl border-slate-200 focus:border-[#0066FF] focus:ring-[#0066FF]"
                        data-testid="select-time"
                      >
                        <SelectValue placeholder="Select preferred time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Morning (9 AM - 12 PM)">Morning (9 AM - 12 PM)</SelectItem>
                        <SelectItem value="Afternoon (12 PM - 4 PM)">Afternoon (12 PM - 4 PM)</SelectItem>
                        <SelectItem value="Evening (4 PM - 7 PM)">Evening (4 PM - 7 PM)</SelectItem>
                        <SelectItem value="Flexible">I'm flexible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Questions */}
                  <div className="space-y-2">
                    <Label htmlFor="questions" className="text-slate-700 font-medium flex items-center gap-2">
                      <MessageSquare size={16} className="text-slate-400" />
                      What would you like to discuss? (Optional)
                    </Label>
                    <Textarea
                      id="questions"
                      name="questions"
                      placeholder="Any specific questions or topics you'd like us to cover..."
                      value={formData.questions}
                      onChange={handleChange}
                      className="min-h-[100px] rounded-xl border-slate-200 focus:border-[#0066FF] focus:ring-[#0066FF] resize-none"
                      data-testid="input-questions"
                    />
                  </div>

                  {/* Resume Upload */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium flex items-center gap-2">
                      <FileText size={16} className="text-slate-400" />
                      Upload Your Resume (Optional)
                    </Label>
                    
                    {formData.resume_filename ? (
                      <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <FileText size={20} className="text-green-600" />
                        </div>
                        <div className="flex-grow">
                          <p className="text-sm font-medium text-green-800">{formData.resume_filename}</p>
                          <p className="text-xs text-green-600">Uploaded successfully</p>
                        </div>
                        <button
                          type="button"
                          onClick={removeResume}
                          className="w-8 h-8 bg-green-100 hover:bg-green-200 rounded-full flex items-center justify-center transition-colors"
                        >
                          <X size={16} className="text-green-700" />
                        </button>
                      </div>
                    ) : (
                      <div 
                        className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                          isUploading 
                            ? 'border-blue-300 bg-blue-50' 
                            : 'border-slate-200 hover:border-[#0066FF] hover:bg-slate-50'
                        }`}
                        onClick={() => !isUploading && fileInputRef.current?.click()}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileUpload}
                          className="hidden"
                          data-testid="input-resume"
                        />
                        
                        {isUploading ? (
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 size={32} className="text-[#0066FF] animate-spin" />
                            <p className="text-sm text-[#0066FF] font-medium">Uploading...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                              <Upload size={24} className="text-slate-400" />
                            </div>
                            <p className="text-sm text-slate-600">
                              <span className="text-[#0066FF] font-medium">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-slate-400">PDF, DOC, or DOCX (max 5MB)</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <p className="text-xs text-slate-500">
                      Uploading your resume helps us prepare for a more personalized consultation.
                    </p>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600 text-sm">
                      {error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#0066FF] hover:bg-blue-700 text-white rounded-xl py-6 text-lg font-semibold transition-all disabled:opacity-70"
                    data-testid="submit-consultation-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={20} className="mr-2 animate-spin" />
                        Booking...
                      </>
                    ) : (
                      <>
                        Book Free Consultation
                        <ArrowRight size={20} className="ml-2" />
                      </>
                    )}
                  </Button>

                  <p className="text-center text-slate-500 text-sm">
                    No commitment required. Just a friendly chat about your career goals.
                  </p>
                </form>
              </div>
            </div>

            {/* Side Info */}
            <div className="lg:col-span-2">
              <div className="sticky top-24 space-y-6">
                {/* What to Expect */}
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                  <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">
                    What to Expect
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-[#0066FF] rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Quick intro call</p>
                        <p className="text-slate-600 text-sm">15-20 minutes, no pressure</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-[#0066FF] rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Understand your goals</p>
                        <p className="text-slate-600 text-sm">We'll learn about your situation</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-[#0066FF] rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Get honest advice</p>
                        <p className="text-slate-600 text-sm">Whether we're a fit or not</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ready to Apply */}
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-6">
                  <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-2">
                    Already Decided?
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Skip the call and apply directly to the program.
                  </p>
                  <Link to="/apply">
                    <Button
                      variant="outline"
                      className="w-full border-amber-300 hover:bg-amber-100 text-slate-900 rounded-xl py-3"
                    >
                      Apply Now
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                </div>

                {/* Contact */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-2">
                    Prefer WhatsApp?
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Message us directly for quick questions.
                  </p>
                  <a href="https://wa.me/919311510040" target="_blank" rel="noopener noreferrer">
                    <Button
                      className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white rounded-xl py-3"
                    >
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current mr-2">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Chat on WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
